import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/** A product-reference outfit, resolved against the actual native Shopify variants. */
class LoneCompleteLook extends Component {
  requiredRefs = ['form', 'submit', 'count', 'total', 'status'];
  #cards = [];
  #busy = false;
  #request;
  #listeners;
  #tiers = [];

  #syncMain = (event) => {
    const picker = event?.target || [...document.querySelectorAll('variant-picker')].find(node => node.dataset.productId === this.dataset.productId && !node.closest('product-card, quick-add-dialog'));
    if (!picker || picker.dataset.productId !== this.dataset.productId || picker.closest('product-card, quick-add-dialog')) return;
    const choices = picker.getAllSelectedOptions?.() || [...picker.querySelectorAll('input:checked, option:checked[data-option-name]')].map(node => ({name: node.dataset.optionName, value: node.value}));
    const main = this.#cards.find(card => card.node.dataset.productId === this.dataset.productId);
    if (!main) return;
    for (const select of main.selects) {
      const chosen = choices.find(option => option.name.toLowerCase() === select.dataset.optionName.toLowerCase());
      if (chosen && [...select.options].some(option => option.value === chosen.value)) select.value = chosen.value;
      else if (select.querySelector('option[value=""]')) select.value = '';
    }
    this.updateLook();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#readCards();
    this.#tiers = JSON.parse(this.dataset.tiers || '[]');
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    document.addEventListener(StandardEvents.productSelect, this.#syncMain, { signal: this.#listeners.signal });
    this.#syncMain();
    this.updateLook();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#readCards();
    this.updateLook();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
    this.#listeners?.abort();
  }

  #readCards() {
    this.#cards = [...this.querySelectorAll('[data-look-card]')].map(node => ({
      node,
      include: node.querySelector('[data-include]'),
      selects: [...node.querySelectorAll('select[data-option-index]')],
      variants: JSON.parse(node.querySelector('[data-look-variants]').textContent),
      image: node.querySelector('img'),
      message: node.querySelector('[data-variant-message]'),
      price: node.querySelector('[data-card-price]'),
      variant: null,
    }));
  }

  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency || 'USD',
    }).format(cents / 100);
  }

  updateLook() {
    let count = 0;
    let total = 0;
    let ready = true;
    for (const card of this.#cards) {
      const included = card.include.checked && !card.include.disabled;
      card.node.toggleAttribute('data-excluded', !included);
      for (const select of card.selects) select.disabled = !included || this.#busy;
      const options = card.selects.map(select => select.value);
      card.variant = card.variants.find(variant => card.selects.length === 0 || variant.options.every((value, index) => value === options[index]));
      // Keep sizes visible while disabling only combinations known to be unavailable.
      for (const [index, select] of card.selects.entries()) {
        for (const option of select.options) {
          if (!option.value) continue;
          option.disabled = !card.variants.some(variant => variant.available && variant.options[index] === option.value && variant.options.every((value, other) => other === index || !options[other] || value === options[other]));
        }
      }
      const complete = card.selects.every(select => select.value !== '');
      card.message.textContent = included && complete && !card.variant?.available ? this.dataset.unavailable : '';
      if (card.variant) {
        card.price.textContent = this.#money(card.variant.price);
      }
      const imageVariant = card.variant || card.variants.find(variant => variant.image && variant.options.every((value, index) => !options[index] || value === options[index]));
      if (imageVariant?.image && card.image && card.image.getAttribute('src') !== imageVariant.image) {
        card.image.removeAttribute('srcset');
        card.image.src = imageVariant.image;
      }
      if (!included) continue;
      count += 1;
      if (!card.variant?.available || !complete) ready = false;
      else total += card.variant.price;
    }
    this.refs.count.textContent = count ? this.dataset.count.replace('[count]', String(count)) : this.dataset.empty;
    const tier = [...this.#tiers].sort((a, b) => b.minimum - a.minimum).find(tier => count >= tier.minimum);
    const rate = tier?.percentage || 0;
    const savings = ready ? this.#cards.filter(card => card.include.checked && !card.include.disabled).reduce((sum, card) => sum + Math.round(card.variant.price * rate), 0) : 0;
    this.refs.total.textContent = count && ready ? this.#money(total - savings) : count ? this.dataset.choose : '';
    this.refs.subtotal.textContent = count && ready ? this.#money(total) : '—';
    this.refs.savings.textContent = count && ready ? `${rate ? `${Math.round(rate * 100)}% · ` : ''}−${this.#money(savings)}` : '—';
    this.refs.estimate.hidden = !count || !ready;
    this.refs.submit.disabled = this.#busy || !count || !ready;
    this.refs.submit.textContent = this.#busy ? this.dataset.adding : this.dataset.add;
  }

  /** Add the selected variants in one native Ajax Cart request; never automatically retry a POST. */
  async addLook(event) {
    event.preventDefault();
    if (this.#busy) return;
    this.updateLook();
    if (this.refs.submit.disabled || !this.refs.form.reportValidity()) return;
    const items = this.#cards.filter(card => card.include.checked && !card.include.disabled).map(card => ({ id: card.variant.id, quantity: 1 }));
    this.#busy = true;
    this.#request = new AbortController();
    this.refs.status.textContent = '';
    this.updateLook();
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    }));
    let added = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(node => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: location.pathname }),
        signal: this.#request.signal,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
      added = true;
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, signal: this.#request.signal });
      if (!cartResponse.ok) throw new Error(this.dataset.error);
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'lone-complete-look', sourceId: this.id, itemCount: items.length, sections: result.sections, didError: false },
      });
      this.refs.status.textContent = this.dataset.added;
    } catch (error) {
      deferred.reject(error);
      if (error.name !== 'AbortError') {
        // An add may have succeeded even when the following cart refresh failed.
        this.refs.status.textContent = added ? this.dataset.added : (error.message || this.dataset.error);
        this.dispatchEvent(new CartErrorEvent({ error: error.message || this.dataset.error, code: 'SERVICE_UNAVAILABLE' }));
      }
    } finally {
      this.#busy = false;
      this.#request = undefined;
      if (this.isConnected) this.updateLook();
    }
  }
}

if (!customElements.get('lone-complete-look')) customElements.define('lone-complete-look', LoneCompleteLook);
